import { useContext, useState } from 'react';
import { DisableContext } from '../../../contexts/DisableContextProvider';
import React from 'react';
import { DragDropContext, Draggable, DraggableProvided, Droppable, DroppableProvided, DropResult } from 'react-beautiful-dnd';
import { Autocomplete, Box, Chip, GridLegacy as Grid, IconButton, TextField } from '@mui/material';
import { UserSettingsCollapseKeys } from '../../../types/User';
import { Field, FieldOption, FieldType, TemplateField } from '../../../types/FieldCommon';
import { Add, Delete, DoNotDisturb, Menu } from '@mui/icons-material';

import _ from 'lodash';
import ConfirmationModal from '../../ConfirmationModal';
import { Loading } from '../../../uikit';
import { handleSplitInput } from '../../../utility/fieldUtils';
import ResponsiveButton from '../../../uikit/buttons/ResponsiveButton';
import AccordionSection from '../../AccordionSection';

export const TYPE_OPTIONS: FieldOption[] = [
  { label: 'Text', value: FieldType.STRING },
  { label: 'Multiline Text', value: FieldType.MULTILINE },
  { label: 'Number', value: FieldType.NUMBER },
  { label: 'Date', value: FieldType.DATE },
  { label: 'Date Time', value: FieldType.DATETIME },
  { label: 'Time', value: FieldType.TIME },
  { label: 'Selection', value: FieldType.AUTOCOMPLETE },
  { label: 'Multiple Selection', value: FieldType.MULTISELECT },
  { label: 'Checkbox', value: FieldType.CHECKBOX },
];

interface ITemplateFieldsSectionProps {
  fields: TemplateField[];
  setFields: (fields: TemplateField[]) => void;
  collapseId: UserSettingsCollapseKeys;
}

export default function TemplateFieldsSection({ collapseId, fields, setFields }: ITemplateFieldsSectionProps) {
  const [deleteFieldIndex, setDeleteFieldIndex] = useState<number | null>(null);
  const [currentOptionInputs, setCurrentOptionInputs] = useState<{ [index: number]: string }>({});

  const { disabled } = useContext(DisableContext);

  if (!fields) return <Loading />;

  const addField = () => {
    setFields([...fields, { label: '', type: FieldType.STRING, uuid: crypto.randomUUID() }]);
  };

  const setFieldElement = (index: number, key: string, value: any) => {
    const newField = { ...fields[index], [key]: value };
    if (key === 'type' && ![FieldType.AUTOCOMPLETE, FieldType.MULTISELECT].includes(value)) {
      delete newField.options;
    }
    setFields(_.map(fields, (field, i) => (i === index ? newField : field)));
  };

  const handleDeleteField = () => {
    if (deleteFieldIndex === null) return;

    setFields(_.reject(fields, (_field, i) => i === deleteFieldIndex));
    setDeleteFieldIndex(null);
  };

  const handleInputChange = (index: number, event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCurrentOptionInputs((prev) => ({
      ...prev,
      [index]: event.target.value,
    }));
  };

  const handleOptionInputKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (currentOptionInputs[index]?.trim()) {
        handleAddOption(index, currentOptionInputs[index].trim());
        setCurrentOptionInputs((prev) => ({
          ...prev,
          [index]: '',
        }));
      }
    }
  };

  const handleOptionInputBlur = (index: number) => {
    if (currentOptionInputs[index]?.trim()) {
      handleAddOption(index, currentOptionInputs[index].trim());
      setCurrentOptionInputs((prev) => ({
        ...prev,
        [index]: '',
      }));
    }
  };

  const handleAddOption = (index: number, optionsString: string) => {
    const currentOptions = fields[index].options || [];
    handleSplitInput(
      optionsString,
      currentOptions,
      (newOptions) => setFieldElement(index, 'options', newOptions),
      (option) => option
    );
  };

  const handleDeleteOption = (index: number, option: string) => {
    const currentOptions = fields[index].options || [];
    const newOptions = _.reject(currentOptions, (opt: string) => opt === option);
    setFieldElement(index, 'options', newOptions);
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    setFields(reorder(fields, result.source.index, result.destination.index));
  };

  /**
   * Reorders the fields in the template's fields array after a drag-and-drop operation.
   * @param list The array of fields to reorder.
   * @param startIndex The index of the field to move.
   * @param endIndex The destination index.
   * @returns The reordered array of fields.
   */
  const reorder = (list: any[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  return (
    <AccordionSection
      title="Fields"
      headerElements={
        <ResponsiveButton startIcon={<Add />} variant="outlined" onClick={addField} disabled={disabled}>
          Add
        </ResponsiveButton>
      }
      collapseId={collapseId}
    >
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="fields-droppable">
          {(provided: DroppableProvided) => (
            <Grid container {...provided.droppableProps} ref={provided.innerRef} sx={{ gap: 2, py: 2 }}>
              {_.map(fields, (field: Field, index: number) => (
                <Draggable key={index} draggableId={`draggable-${index}`} index={index} isDragDisabled={disabled}>
                  {(provided: DraggableProvided) => (
                    <Grid
                      item
                      container
                      xs={12}
                      key={index}
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      sx={{ alignItems: 'center', position: 'relative', '&:hover .menu-icon': { visibility: 'visible' } }}
                    >
                      <Grid item sx={{ display: 'flex', alignItems: 'center', px: 1, visibility: 'hidden' }} className="menu-icon">
                        {disabled ? <DoNotDisturb /> : <Menu />}
                      </Grid>
                      <Grid item xs={10} sx={{ px: 1 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                          <TextField
                            id="label"
                            label="Label"
                            onChange={(event) => setFieldElement(index, event.target.id, event.target.value)}
                            value={field?.label ?? ''}
                            disabled={disabled}
                          />
                          <Autocomplete<FieldOption>
                            options={TYPE_OPTIONS}
                            onChange={(_, fieldOption) => setFieldElement(index, 'type', fieldOption?.value)}
                            getOptionLabel={(option) => option.label}
                            value={_.find(TYPE_OPTIONS, (option) => option.value === field.type)}
                            renderInput={(params) => <TextField {...params} label="Type" />}
                            disabled={disabled}
                          />
                        </Box>
                        {[FieldType.AUTOCOMPLETE, FieldType.MULTISELECT].includes(field.type) && (
                          <Grid item xs={12} sx={{ py: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <TextField
                                label="Options"
                                placeholder="Enter options separated by commas"
                                onKeyDown={(e) => handleOptionInputKeyDown(index, e as React.KeyboardEvent<HTMLInputElement>)}
                                onBlur={() => handleOptionInputBlur(index)}
                                onChange={(e) => handleInputChange(index, e)}
                                value={currentOptionInputs[index] || ''}
                                disabled={disabled}
                                fullWidth
                              />
                            </Box>
                            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {field.options?.map((option: string, optIndex: number) => (
                                <Chip key={optIndex} label={option} onDelete={disabled ? undefined : () => handleDeleteOption(index, option)} />
                              ))}
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                      <Grid item xs={1} sx={{ textAlign: 'center', alignContent: 'center' }}>
                        <IconButton onClick={() => setDeleteFieldIndex(index)} disabled={disabled}>
                          <Delete />
                        </IconButton>
                      </Grid>
                    </Grid>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Grid>
          )}
        </Droppable>
      </DragDropContext>
      <ConfirmationModal
        text="Remove this field?"
        isOpen={deleteFieldIndex !== null}
        onConfirm={handleDeleteField}
        onCancel={() => setDeleteFieldIndex(null)}
      />
    </AccordionSection >
  );
}
